import React from 'react';
import { Box, Button, Typography, Container } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import * as Sentry from '@sentry/react';

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetError }) => {
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center',
          py: 4,
        }}
      >
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Something went wrong
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          We've been notified and are working to fix the issue.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={resetError}>
            Try Again
          </Button>
          <Button variant="outlined" onClick={() => window.location.href = '/dashboard'}>
            Go to Dashboard
          </Button>
        </Box>
        {import.meta.env.DEV && (
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1, width: '100%' }}>
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
              {error.message}
              {'\n\n'}
              {error.stack}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

interface Props {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
}

export const ErrorBoundary: React.FC<Props> = ({ children, fallback }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }: { error: unknown; resetError: () => void }) => {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        const FallbackComponent = fallback || ErrorFallback;
        return <FallbackComponent error={errorObj} resetError={resetError} />;
      }}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
